import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import { LANGUAGES } from '../constants/Languages';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';

const ANY_LANGUAGE = 'Any language is fine';

// Scoped to therapists only — graphologists use a separate, unrelated filter
// set (Personality Profiling, Career Guidance, etc.), not the 14-category
// specialisation list this quiz scores against.
//
// Every option either adds to specialisation scores (`categories`) or, on the
// language question, records the person's preferred language (`language`).
const QUESTIONS = [
  {
    id: 'q1',
    prompt: "What's weighing on you most right now?",
    options: [
      { label: 'Constant worry or racing thoughts', categories: ['Anxiety'] },
      { label: 'Feeling numb, low, or unmotivated', categories: ['Depression'] },
      { label: "Running on empty, can't switch off", categories: ['Stress & Burnout'] },
      { label: "Something from the past I can't shake", categories: ['Trauma & PTSD'] },
      { label: "I don't know — something just feels off", categories: ['Self-Esteem & Life Adjustment'] },
    ]
  },
  {
    id: 'q2',
    prompt: 'Where does most of the pressure come from?',
    options: [
      { label: 'Work', categories: ['Career & Life Transitions'] },
      { label: 'School or exams', categories: ['Academic & Exam Pressure'] },
      { label: 'Family or parenting', categories: ['Family & Parenting'] },
      { label: 'A relationship', categories: ['Relationships'] },
      { label: "A habit or substance I can't control", categories: ['Addiction & Recovery'] },
    ]
  },
  {
    id: 'q3',
    prompt: 'Who is this for?',
    options: [
      { label: 'Me, as an adult', categories: [] },
      { label: 'My child or teenager', categories: ['Child & Adolescent'] },
      { label: 'Me — I suspect ADHD, autism, or something similar', categories: ['Neurodevelopmental'] },
      { label: 'Someone managing an illness, injury, or disability', categories: ['Rehabilitation & Disability'] },
    ]
  },
  {
    id: 'q4',
    prompt: 'How do you usually respond under pressure?',
    options: [
      { label: 'I shut down or avoid it', categories: ['Depression'] },
      { label: 'I spiral into worry', categories: ['Anxiety'] },
      { label: 'I lash out or act on impulse', categories: ['Behavioural Therapy'] },
      { label: 'I keep going until I burn out', categories: ['Stress & Burnout'] },
    ]
  },
  {
    id: 'q5',
    prompt: 'How long has this been building?',
    options: [
      { label: 'Just the last few weeks', categories: ['Stress & Burnout'] },
      { label: 'Months', categories: ['Depression'] },
      { label: 'Years, on and off', categories: ['Trauma & PTSD'] },
      { label: 'It started with one big change in my life', categories: ['Career & Life Transitions'] },
    ]
  },
  {
    id: 'q6',
    kind: 'language',
    prompt: 'Which language would you feel most comfortable talking in?',
    hint: "You'll be matched with therapists who speak it.",
    options: [
      ...LANGUAGES.map(lang => ({ label: lang, language: lang })),
      { label: ANY_LANGUAGE, language: null },
    ]
  },
];

const MatchingQuiz = () => {
  const navigate = useNavigate();
  // One chosen option per answered question. Keeping the answers (rather than a
  // running score) is what makes "Back" possible — scores are derived from them.
  const [answers, setAnswers] = useState([]);
  const [matches, setMatches] = useState(null);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [errorLoading, setErrorLoading] = useState(false);

  const step = answers.length;
  const isResults = step === QUESTIONS.length;

  const { topCategories, preferredLanguage } = useMemo(() => {
    const scores = {};
    let language = null;
    answers.forEach(opt => {
      (opt.categories || []).forEach(cat => { scores[cat] = (scores[cat] || 0) + 1; });
      if (opt.language) language = opt.language;
    });

    const ranked = Object.entries(scores)
      .filter(([, score]) => score > 0)
      .sort((a, b) => b[1] - a[1]);

    // Top 2, but include a tied 3rd rather than arbitrarily cutting a tie.
    const cutoff = ranked[1]?.[1];
    const top = ranked.filter(([, score]) => cutoff === undefined || score >= cutoff).slice(0, 3);
    return { topCategories: top.map(([name]) => name), preferredLanguage: language };
  }, [answers]);

  const loadMatches = async (categories, language) => {
    setLoadingMatches(true);
    setErrorLoading(false);
    try {
      const { data } = await axios.get('/doctors');
      const list = Array.isArray(data) ? data : (data?.data || []);

      const scored = list
        .map(doc => {
          const specs = Array.isArray(doc.specialisations) ? doc.specialisations : [];
          const langs = Array.isArray(doc.languages) ? doc.languages : [];
          const matchedSpecs = specs.filter(s => categories.includes(s));
          const speaksLanguage = !!language && langs.includes(language);
          // Language is weighted above a single specialisation overlap: a
          // therapist you cannot talk to comfortably is not a good match no
          // matter how well the specialisation fits.
          const score = matchedSpecs.length * 2 + (speaksLanguage ? 3 : 0);
          return { ...doc, matchedSpecs, speaksLanguage, score };
        })
        .filter(doc => doc.score > 0 || (categories.length === 0 && !language))
        .sort((a, b) => b.score - a.score);

      setMatches(scored.slice(0, 4));
    } catch (error) {
      setErrorLoading(true);
    } finally {
      setLoadingMatches(false);
    }
  };

  const handleAnswer = (option) => {
    const next = [...answers, option];
    setAnswers(next);

    if (next.length === QUESTIONS.length) {
      const scores = {};
      let language = null;
      next.forEach(opt => {
        (opt.categories || []).forEach(cat => { scores[cat] = (scores[cat] || 0) + 1; });
        if (opt.language) language = opt.language;
      });
      const ranked = Object.entries(scores).sort((a, b) => b[1] - a[1]);
      const cutoff = ranked[1]?.[1];
      const cats = ranked.filter(([, s]) => cutoff === undefined || s >= cutoff).slice(0, 3).map(([n]) => n);
      loadMatches(cats, language);
    }
  };

  const goBack = () => setAnswers(prev => prev.slice(0, -1));

  const restart = () => {
    setAnswers([]);
    setMatches(null);
    setErrorLoading(false);
  };

  const q = QUESTIONS[step];
  const nobodySpeaksLanguage =
    !!preferredLanguage && matches && matches.length > 0 && !matches.some(m => m.speaksLanguage);

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {!isResults ? (
          <>
            <div style={styles.progressRow}>
              {QUESTIONS.map((_, i) => (
                <div key={i} style={{ ...styles.progressDot, background: i <= step ? '#e8622a' : 'rgba(48,60,108,0.12)' }} />
              ))}
            </div>
            <p style={styles.stepLabel}>Question {step + 1} of {QUESTIONS.length}</p>
            <h2 style={styles.prompt}>{q.prompt}</h2>
            {q.hint && <p style={styles.hint}>{q.hint}</p>}

            <div style={q.kind === 'language' ? styles.optionsGrid : styles.options}>
              {q.options.map(opt => (
                <button
                  key={opt.label}
                  onClick={() => handleAnswer(opt)}
                  style={q.kind === 'language' ? styles.pillBtn : styles.optionBtn}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {step > 0 && (
              <button onClick={goBack} style={styles.backBtn}>← Back</button>
            )}
          </>
        ) : (
          <>
            <span style={styles.eyebrow}>Your matches</span>
            <h2 style={styles.resultsTitle}>
              {topCategories.length > 0
                ? `Based on what you shared, these areas stood out: ${topCategories.join(', ')}`
                : "We couldn't narrow it down from your answers — here are a few therapists to start with."}
            </h2>
            {preferredLanguage && (
              <p style={styles.langLine}>Preferred language: <strong>{preferredLanguage}</strong></p>
            )}

            {nobodySpeaksLanguage && (
              <p style={styles.warn}>
                None of these therapists list {preferredLanguage} yet. They match your concerns, and you can browse the
                full directory for more.
              </p>
            )}

            {loadingMatches ? (
              <p style={styles.muted}>Finding your matches…</p>
            ) : errorLoading ? (
              <p style={styles.muted}>Couldn't load matches right now. Please try the full directory instead.</p>
            ) : matches && matches.length > 0 ? (
              <div style={styles.matchGrid}>
                {matches.map((doc, idx) => (
                  <div key={doc.id} style={styles.matchCard}>
                    <img
                      src={doc.image ? `${BACKEND_URL}/uploads/${doc.image}` : '/img/6971412.png'}
                      alt={doc.name}
                      onError={(e) => { e.target.src = '/img/6971412.png'; }}
                      style={styles.matchAvatar}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={styles.matchName}>
                        {doc.name}
                        {idx === 0 && <span style={styles.bestTag}>Best match</span>}
                      </p>
                      <p style={styles.matchSpec}>
                        {(doc.specialisations || []).slice(0, 2).join(', ') || 'General Therapy'}
                      </p>
                      {/* Why this person was suggested — the part the old quiz never said. */}
                      <div style={styles.whyRow}>
                        {doc.speaksLanguage && <span style={styles.whyChipLang}>Speaks {preferredLanguage}</span>}
                        {doc.matchedSpecs.slice(0, 2).map(s => (
                          <span key={s} style={styles.whyChip}>{s}</span>
                        ))}
                      </div>
                    </div>
                    <button onClick={() => navigate(`/bookings/${doc.id}`)} style={styles.matchBtn}>
                      Book
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p style={styles.muted}>
                No exact matches yet, but our full directory has therapists across every area.
              </p>
            )}

            <div style={styles.resultsActions}>
              <button onClick={restart} style={styles.secondaryBtn}>Retake quiz</button>
              <Link
                to={topCategories[0] ? `/doctors?tab=therapists&focus=${encodeURIComponent(topCategories[0])}` : '/doctors?tab=therapists'}
                style={styles.primaryBtn}
              >
                See all matching therapists
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  page: { minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px', background: '#f9fafc' },
  card: { background: 'white', borderRadius: 24, padding: 40, maxWidth: 580, width: '100%', boxShadow: '0 12px 32px rgba(48,60,108,0.08)', border: '1px solid rgba(48,60,108,0.06)' },
  progressRow: { display: 'flex', gap: 6, marginBottom: 20 },
  progressDot: { flex: 1, height: 4, borderRadius: 2, transition: 'background 0.3s ease' },
  stepLabel: { fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6b7280', margin: '0 0 8px' },
  prompt: { fontFamily: "'DM Serif Display', serif", fontSize: 24, color: '#1a2344', margin: '0 0 24px' },
  hint: { fontSize: 14, color: '#6b7280', margin: '-12px 0 20px' },
  options: { display: 'flex', flexDirection: 'column', gap: 10 },
  optionsGrid: { display: 'flex', flexWrap: 'wrap', gap: 10 },
  optionBtn: { textAlign: 'left', padding: '14px 18px', borderRadius: 12, border: '1.5px solid rgba(48,60,108,0.12)', background: 'white', fontSize: 14, color: '#374151', cursor: 'pointer', transition: 'all 0.2s ease' },
  pillBtn: { padding: '10px 20px', borderRadius: 50, border: '1.5px solid rgba(48,60,108,0.15)', background: 'white', fontSize: 14, fontWeight: 600, color: '#303c6c', cursor: 'pointer', transition: 'all 0.2s ease' },
  backBtn: { marginTop: 24, background: 'none', border: 'none', color: '#6b7280', fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: 0 },
  eyebrow: { fontSize: 12, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#4a579d' },
  resultsTitle: { fontFamily: "'DM Serif Display', serif", fontSize: 20, color: '#1a2344', margin: '8px 0 12px', lineHeight: 1.4 },
  langLine: { fontSize: 14, color: '#374151', margin: '0 0 20px' },
  warn: { fontSize: 13, color: '#8a5a1c', background: '#fdf3e3', border: '1px solid #f2dcb4', borderRadius: 12, padding: '10px 14px', margin: '0 0 16px', lineHeight: 1.5 },
  muted: { color: '#6b7280', fontSize: 14 },
  matchGrid: { display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 },
  matchCard: { display: 'flex', alignItems: 'center', gap: 12, padding: 12, border: '1px solid rgba(48,60,108,0.08)', borderRadius: 14 },
  matchAvatar: { width: 52, height: 52, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 },
  matchName: { margin: 0, fontWeight: 600, fontSize: 15, color: '#1a2344', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  bestTag: { fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', background: '#e8622a', color: '#fff', padding: '2px 8px', borderRadius: 50 },
  matchSpec: { margin: '2px 0 0', fontSize: 12, color: '#6b7280' },
  whyRow: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 },
  whyChipLang: { fontSize: 11, fontWeight: 600, background: '#e8f2ef', color: '#4a7c6f', padding: '3px 10px', borderRadius: 50 },
  whyChip: { fontSize: 11, fontWeight: 600, background: 'rgba(74,87,157,0.08)', color: '#4a579d', padding: '3px 10px', borderRadius: 50 },
  matchBtn: { border: 'none', background: '#1a2344', color: 'white', padding: '8px 16px', borderRadius: 50, fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 0 },
  resultsActions: { display: 'flex', gap: 10 },
  primaryBtn: { flex: 1, textAlign: 'center', padding: '13px 20px', borderRadius: 50, border: 'none', background: '#1a2344', color: 'white', fontWeight: 600, fontSize: 14, textDecoration: 'none' },
  secondaryBtn: { flex: 1, padding: '13px 20px', borderRadius: 50, border: '1.5px solid rgba(48,60,108,0.15)', background: 'white', color: '#374151', fontWeight: 600, fontSize: 14, cursor: 'pointer' }
};

export default MatchingQuiz;